import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Typography, Collapse, Tooltip } from "antd";
import {
  CheckSquareOutlined,
  RightOutlined,
  PictureOutlined,
  FormOutlined,
  FileDoneOutlined,
  SolutionOutlined
} from "@ant-design/icons";
import styled from "styled-components";

import { WHITE, GRAY_200, GRAY_400, GRAY_500, GRAY_700, PRIMARY_50, PRIMARY_600 } from "../../constant/colors";
import { BetaTagContainer, BetaTagText } from "../../component/AIChat/Styles";
import { CountPill } from "./taskStyles";
import { AgenticStatsHeader } from "./AgenticStatsHeader";
import { ScreenshotTasksSection } from "./ScreenshotTasksSection";
import { HitlTasksSection } from "./HitlTasksSection";
import { HITL_TASK_TYPES } from "../../constant/agenticPlatform";
import { useGetSetting } from "../../hooks/getSetting";

const { Title, Text } = Typography;
const { Panel } = Collapse;

// Collapse panel keys — also the stable identity for each section.
const SECTION = {
  SCREENSHOTS: "screenshots",
  EMR_FILL: "emr_fill",
  CONSENT: "consent",
  EE_REVIEW: "ee_review"
};

const PageContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 20px 24px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  min-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled(Text)`
  color: ${GRAY_500};
  font-size: 13px;
  display: block;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  font-weight: 500;
  color: ${GRAY_700};
`;

// Soft-tinted square behind each section's glyph — the same PRIMARY_50/600
// pairing as the hub's action pills, so the section headers and their row
// actions read as one family.
const SectionIcon = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${PRIMARY_50};
  color: ${PRIMARY_600};
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

// Each panel is a standalone card (the mockup's `.sec`): own border, rounded,
// spaced. `bordered={false}` drops antd's default outer frame + dividers so
// these per-item borders stand alone. Section bodies own their padding (the
// tables run flush to the card edges).
const SectionsCollapse = styled(Collapse)`
  background: transparent;

  .ant-collapse-item {
    background: ${WHITE};
    border: 1px solid ${GRAY_200};
    border-radius: 10px;
    margin-bottom: 12px;
    overflow: hidden;
  }
  /* Each panel is a standalone card. antd's borderless Collapse special-cases
     the LAST item — assuming items are dividers in one shared frame — and
     zeroes both its corner radius (collapse/style/index.css:108) and its
     bottom border (index.css:111). We render separate cards, so "&&"
     out-specifies those rules to restore the rounded corners + bottom border;
     the item's overflow:hidden then clips the expanded content to the box. */
  && .ant-collapse-item:last-child {
    border-bottom: 1px solid ${GRAY_200};
    border-radius: 10px;
  }
  .ant-collapse-header {
    align-items: center !important;
    padding: 12px 16px !important;
  }
  .ant-collapse-content > .ant-collapse-content-box {
    padding: 0;
  }
`;

/**
 * Agentic Platform — Tasks hub. One page for everything a navigator /
 * operator needs to act on, replacing the pipeline board + the standalone
 * EMR Tasks page. A general-statistics strip up top, then stacked,
 * collapsible card sections, each with a count chip in its header (visible
 * while collapsed) and its own 5/page pager:
 *
 *   1. **EMR screenshots** — bulk-upload UX: stage a mixed-patient batch,
 *      assign it to pending tasks, submit. (`ScreenshotTasksSection`)
 *   2. **Manual EMR fill** — `emr_fill` HITL blockers; the row / "Fill EMR
 *      data" opens the fill modal. (`HitlTasksSection`)
 *   3. **Consent decisions** — `consent_decide` HITL; the row / "Review"
 *      opens the approve/decline modal. (`HitlTasksSection`)
 *   4. **Express enrollments** — `ee_review` HITL; the row / "Review enrollment"
 *      opens the real EE form, prefilled and editable. (`HitlTasksSection`)
 *
 * Each section fetches its own data and reports its total up so the
 * collapsed headers stay accurate (panels use `forceRender`). Freshness is
 * automatic — every section (and the stats strip) refetches each minute, so
 * the page carries no manual refresh chrome.
 */
const AgenticPlatformTasks = () => {
  const { t } = useTranslation();

  // Deep-link target from the patient-page AgenticRunsModal: `?agenticRunId=<id>`.
  // Passed to both HITL sections; whichever section's feed contains the run
  // auto-opens its modal once (the other ignores it).
  // A site with EMR data turned off never produces screenshot or manual-fill
  // work, so those two sections could only ever sit empty — and an empty section
  // still tells the navigator a capability exists that this site doesn't have.
  // Gate at render, not by emptying the lists.
  const [isEmrDataEnabled] = useGetSetting(["agenticUseEmrDataForValidation"]);

  const location = useLocation();
  const deepLinkRunId = new URLSearchParams(location.search).get("agenticRunId");

  // Per-section totals for the collapsible headers. Stable setters so the
  // sections' count-reporting effects don't loop.
  const [counts, setCounts] = useState({ screenshots: 0, emrFill: 0, consent: 0, eeReview: 0 });
  const setScreenshotsCount = useCallback((c) => setCounts((s) => ({ ...s, screenshots: c })), []);
  const setEmrFillCount = useCallback((c) => setCounts((s) => ({ ...s, emrFill: c })), []);
  const setConsentCount = useCallback((c) => setCounts((s) => ({ ...s, consent: c })), []);
  const setEeReviewCount = useCallback((c) => setCounts((s) => ({ ...s, eeReview: c })), []);

  const sectionHeader = (icon, label, count, testId, tooltip) => (
    <PanelHeader data-testid={testId}>
      <SectionIcon>{icon}</SectionIcon>
      <Tooltip title={tooltip}>
        <span>{label}</span>
      </Tooltip>
      <CountPill>{count}</CountPill>
    </PanelHeader>
  );

  // eslint-disable-next-line react/prop-types
  const expandIcon = ({ isActive }) => (
    <RightOutlined rotate={isActive ? 90 : 0} style={{ color: GRAY_400, fontSize: 13 }} />
  );

  return (
    <PageContainer data-testid="agentic-tasks-page">
      <HeaderBlock>
        <HeaderLeft>
          <CheckSquareOutlined style={{ fontSize: 24, color: PRIMARY_600 }} />
          <Title level={3} style={{ margin: 0 }}>
            {t("agentic_tasks.page_title")}
          </Title>
          <BetaTagContainer>
            <BetaTagText>BETA</BetaTagText>
          </BetaTagContainer>
        </HeaderLeft>
        <Subtitle>{t("agentic_tasks.page_subtitle")}</Subtitle>
      </HeaderBlock>

      <AgenticStatsHeader />

      <SectionsCollapse
        bordered={false}
        expandIcon={expandIcon}
        defaultActiveKey={[
          ...(isEmrDataEnabled ? [SECTION.SCREENSHOTS, SECTION.EMR_FILL] : []),
          SECTION.CONSENT,
          SECTION.EE_REVIEW
        ]}
      >
        {isEmrDataEnabled && (
          <>
            <Panel
              key={SECTION.SCREENSHOTS}
              forceRender
              header={sectionHeader(
                <PictureOutlined />,
                t("agentic_tasks.section_screenshots"),
                counts.screenshots,
                "agentic-tasks-header-screenshots",
                t("agentic_tasks.tooltip_screenshots")
              )}
            >
              <ScreenshotTasksSection onCountChange={setScreenshotsCount} />
            </Panel>

            <Panel
              key={SECTION.EMR_FILL}
              forceRender
              header={sectionHeader(
                <FormOutlined />,
                t("agentic_tasks.section_emr_fill"),
                counts.emrFill,
                "agentic-tasks-header-emr-fill",
                t("agentic_tasks.tooltip_emr_fill")
              )}
            >
              <HitlTasksSection
                hitlTaskType={HITL_TASK_TYPES.EMR_FILL}
                actionLabel={t("agentic_tasks.action_fill_emr")}
                contextTitle={t("agentic_tasks.col_reason")}
                emptyText={t("agentic_tasks.empty_emr_fill")}
                onCountChange={setEmrFillCount}
                autoOpenRunId={deepLinkRunId}
              />
            </Panel>
          </>
        )}

        <Panel
          key={SECTION.CONSENT}
          forceRender
          header={sectionHeader(
            <FileDoneOutlined />,
            t("agentic_tasks.section_consent"),
            counts.consent,
            "agentic-tasks-header-consent",
            t("agentic_tasks.tooltip_consent")
          )}
        >
          <HitlTasksSection
            hitlTaskType={HITL_TASK_TYPES.CONSENT_DECIDE}
            actionLabel={t("agentic_tasks.action_review")}
            contextTitle={t("agentic_tasks.col_state")}
            emptyText={t("agentic_tasks.empty_consent")}
            onCountChange={setConsentCount}
            autoOpenRunId={deepLinkRunId}
          />
        </Panel>

        <Panel
          key={SECTION.EE_REVIEW}
          forceRender
          header={sectionHeader(
            <SolutionOutlined />,
            t("agentic_tasks.section_ee_review"),
            counts.eeReview,
            "agentic-tasks-header-ee-review",
            t("agentic_tasks.tooltip_ee_review")
          )}
        >
          <HitlTasksSection
            hitlTaskType={HITL_TASK_TYPES.EE_REVIEW}
            actionLabel={t("agentic_tasks.action_review_enrollment")}
            contextTitle={t("agentic_tasks.col_program")}
            emptyText={t("agentic_tasks.empty_ee_review")}
            onCountChange={setEeReviewCount}
            autoOpenRunId={deepLinkRunId}
          />
        </Panel>
      </SectionsCollapse>
    </PageContainer>
  );
};

export { AgenticPlatformTasks };
